/** biome-ignore-all lint/security/noDangerouslySetInnerHtml: Rich text is delivered from Umbraco. */
import type {
	CustomizableLinkUmbBlockPartialElementModel,
	HeroUmbBlockElementModel,
	HeroUmbBlockSettingsElementModel,
} from "#/_generated/umbraco/delivery-api/models";
import Hero, {
	type HeroContentAlignment,
	type HeroHeadingAppearance,
	type HeroIntent,
	type HeroMedia,
	type HeroVariant,
} from "#/_packages/umbrella/features/hero/components/hero.tsx";
import CustomizableLinkUmbBlock from "#/_packages/umbrella/integrations/umbraco/components/blocks/customizable-link-umb-block.tsx";
import type { BlockItemProps } from "#/_packages/umbrella/integrations/umbraco/types";
import {
	type CmsImageOptions,
	getMediaAlt,
	getResponsiveImageProps,
	resolveMediaUrl,
} from "#/_packages/umbrella/integrations/umbraco/utils/image-utils.ts";
import {
	tryGetAnimationScrollType,
	tryGetAspectRatio,
	tryGetHeadingLevel,
} from "#/_packages/umbrella/utils";
import { useCmsImageOptions } from "../../utils/media-composition";

type HeroBlockProps = BlockItemProps & {
	content: HeroUmbBlockElementModel;
	settings: HeroUmbBlockSettingsElementModel;
};

const heroVariants: HeroVariant[] = ["standard", "side", "background"];
const heroIntents: HeroIntent[] = [
	"primary",
	"secondary",
	"light",
	"dark",
	"none",
];
const heroHeadingAppearances: HeroHeadingAppearance[] = [
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
];
const heroContentAlignments: HeroContentAlignment[] = [
	"start",
	"center",
	"end",
];

function tryGetHeroVariant(value?: string | null): HeroVariant {
	return heroVariants.includes(value as HeroVariant)
		? (value as HeroVariant)
		: "standard";
}

function tryGetHeroIntent(value?: string | null): HeroIntent {
	return heroIntents.includes(value as HeroIntent)
		? (value as HeroIntent)
		: "none";
}

function tryGetHeroHeadingAppearance(
	value?: string | null,
): HeroHeadingAppearance | undefined {
	return heroHeadingAppearances.includes(value as HeroHeadingAppearance)
		? (value as HeroHeadingAppearance)
		: undefined;
}

function tryGetHeroContentAlignment(
	value?: string | null,
): HeroContentAlignment {
	return heroContentAlignments.includes(value as HeroContentAlignment)
		? (value as HeroContentAlignment)
		: "start";
}

function toAbsoluteUmbracoMediaUrl(mediaUrl: string | undefined) {
	return resolveMediaUrl(mediaUrl);
}

function getHeroMedia(
	content: HeroUmbBlockElementModel,
	variant: HeroVariant,
	options: CmsImageOptions,
): HeroMedia | undefined {
	const properties = content.properties;
	const caption = properties?.videoCaption ?? undefined;
	const title = properties?.heading ?? undefined;
	const sharedVideo = {
		title,
		caption,
		autoplay: properties?.videoAutoplay ?? false,
		loopPlayback: properties?.videoLoopPlayback ?? false,
		muteAudio: properties?.videoMuteAudio ?? false,
		hideControls: properties?.videoHideControls ?? false,
		enableInteraction: properties?.videoEnableInteraction ?? false,
	};
	const externalVideo = properties?.videoFromExternalLink?.trim();

	if (externalVideo) {
		return {
			type: "videoExternal",
			src: externalVideo,
			...sharedVideo,
		};
	}

	const mediaLibraryVideo = properties?.videoFromMediaLibrary?.[0];
	const mediaLibraryVideoSrc = toAbsoluteUmbracoMediaUrl(
		mediaLibraryVideo?.url,
	);

	if (mediaLibraryVideoSrc) {
		return {
			type: "videoFile",
			src: mediaLibraryVideoSrc,
			...sharedVideo,
		};
	}

	const image = properties?.image?.[0];
	if (!image?.url) {
		return undefined;
	}

	return {
		type: "image",
		...getResponsiveImageProps(image, {
			width: variant === "standard" ? 1280 : 1300,
			aspectRatio: properties?.aspectRatio,
			...options,
		}),
		alt: getMediaAlt(image),
		caption: properties?.imageCaption ?? undefined,
	};
}

function getHeroActions(content: HeroUmbBlockElementModel) {
	const items = content.properties?.links?.items ?? [];

	const actions = items.flatMap((item) => {
		return item.content
			? [
					<CustomizableLinkUmbBlock
						key={item.content.id}
						content={
							item.content as CustomizableLinkUmbBlockPartialElementModel
						}
					/>,
				]
			: [];
	});

	return actions.length > 0 ? actions : undefined;
}

export default function HeroUmbBlock({
	className,
	content,
	settings,
	mediaPriority,
	mediaSizes,
}: HeroBlockProps) {
	const variant = tryGetHeroVariant(content.properties?.variant);
	const mediaOptions = useCmsImageOptions(
		content.id,
		variant === "side" ? "half" : variant === "standard" ? "hero" : "full",
		mediaPriority,
		mediaSizes,
	);

	return (
		<Hero
			id={settings.properties?.generatedId ?? content.id}
			className={className}
			variant={variant}
			intentColor={tryGetHeroIntent(content.properties?.intentColor)}
			contentAlignment={tryGetHeroContentAlignment(
				settings.properties?.contentAlignment,
			)}
			imagePositionEnd={content.properties?.imagePositionEnd ?? false}
			headingLabel={content.properties?.headingLabel ?? undefined}
			heading={content.properties?.heading ?? undefined}
			headingLevel={tryGetHeadingLevel(content.properties?.headingLevel)}
			headingAppearance={tryGetHeroHeadingAppearance(
				content.properties?.headingAppearance,
			)}
			preamble={
				content.properties?.preamble?.markup ? (
					<div
						className="rich-text"
						dangerouslySetInnerHTML={{
							__html: content.properties.preamble.markup,
						}}
					/>
				) : undefined
			}
			actions={getHeroActions(content)}
			media={getHeroMedia(content, variant, mediaOptions)}
			aspectRatio={tryGetAspectRatio(content.properties?.aspectRatio)}
			animationHoverType={settings.properties?.animationHoverType ?? []}
			animationScrollType={tryGetAnimationScrollType(
				settings.properties?.animationScrollType,
			)}
		/>
	);
}
